import type { NavKey } from '@shared-core/navigation/routes'
import { ScreensList, StackMode } from '@shared-core/navigation/screens-list'

/**
 * Служебная мета exit-cross флоу. Живёт в params маршрута под капотом навигации.
 * Экраны не должны писать её вручную — только читать через `useExitCross`.
 */
export const EXIT_FLOW_PARAM_KEY = '__exitFlow'

export type ExitFlowEntryMeta = {
  // Origin цепочки, которой пользователь пришёл НА flow root (например Profile → Limits → paygine-хаб)
  origin: ScreensList
  // Хопы входящей цепочки, включая переход на сам root
  hop: number
}

export type ExitFlowMeta = {
  // Куда ведёт крестик (корень флоу)
  origin: ScreensList
  // Сколько хопов от origin. Origin-экран = 0; первый child = 1; крестик при hop >= 2
  hop: number
  // Только на flow-root экране: входящая цепочка — root сам может быть глубоким экраном чужого флоу
  // (B9: Profile → Limits → paygine-хаб = 2 хопа → крестик на хабе → Profile)
  entry?: ExitFlowEntryMeta
}

// Хабы, с которых начинается новый флоу (hop сбрасывается)
export const FLOW_ROOTS: ReadonlySet<string> = new Set([
  ScreensList.HOME_START,
  ScreensList.HOME_START,
  // TODO(FE-1669): убрать гейт чатов, когда чаты раскатаны всем — PROFILE_START уходит из AUTHORIZED_TAB_ROOTS,
  // fallback в buildExitFlowResetState становится HOME_START
  // Security — отдельный origin (как Settings): Settings→Security не хоп, а старт security-флоу
  // Paygine-хаб: root для своих шагов (RU passport / Gosuslugi / phones), но глубина входа хранится в entry
])

export function isFlowRoot(key: string | undefined | null): boolean {
  return !!key && FLOW_ROOTS.has(key)
}

export function readExitFlowMeta(params: unknown): ExitFlowMeta | undefined {
  if (!params || typeof params !== 'object') return undefined

  const raw = (params as Record<string, unknown>)[EXIT_FLOW_PARAM_KEY]

  if (!raw || typeof raw !== 'object') return undefined

  const origin = (raw as ExitFlowMeta).origin
  const hop = (raw as ExitFlowMeta).hop

  if (typeof origin !== 'string' || typeof hop !== 'number' || !Number.isFinite(hop)) {
    return undefined
  }

  const rawEntry = (raw as ExitFlowMeta).entry
  const entry =
    rawEntry &&
    typeof rawEntry === 'object' &&
    typeof rawEntry.origin === 'string' &&
    typeof rawEntry.hop === 'number' &&
    Number.isFinite(rawEntry.hop)
      ? { origin: rawEntry.origin, hop: rawEntry.hop }
      : undefined

  return entry ? { origin: origin as ScreensList, hop, entry } : { origin: origin as ScreensList, hop }
}

export type CurrentRouteSnapshot = {
  name?: string
  params?: unknown
} | null

export type ExitFlowInjectOptions = {
  // replace/reset слота: hop не увеличиваем; entry на root не пишем (это выход из флоу, а не вход)
  isReplace?: boolean
}

// Входящая цепочка для навигации НА flow root: root может быть и глубоким экраном чужого флоу
function computeEntryMeta(current: CurrentRouteSnapshot): ExitFlowEntryMeta | undefined {
  const currentMeta = readExitFlowMeta(current?.params)

  if (currentMeta) {
    return { origin: currentMeta.origin, hop: currentMeta.hop + 1 }
  }

  if (isFlowRoot(current?.name)) {
    return { origin: current?.name as ScreensList, hop: 1 }
  }

  return undefined
}

/**
 * Считает meta для целевого маршрута на основе текущего.
 *
 * - navigate НА flow root → { origin: target, hop: 0, entry: входящая цепочка }
 * - navigate С flow root → { origin: currentRoot, hop: 1 }
 * - navigate иначе → наследуем origin, hop+1
 * - replace/reset → тот же слот стека: origin/hop как у текущего (не +1), entry не пишем
 * - fallback (нет meta / диплинк) → hop 1, origin = current root или HOME
 */
export function computeNextExitFlowMeta(
  targetKey: NavKey | string,
  current: CurrentRouteSnapshot,
  options?: ExitFlowInjectOptions,
): ExitFlowMeta {
  const target = String(targetKey)

  if (isFlowRoot(target)) {
    const entry = options?.isReplace ? undefined : computeEntryMeta(current)

    return entry ? { origin: target as ScreensList, hop: 0, entry } : { origin: target as ScreensList, hop: 0 }
  }

  const currentName = current?.name
  const currentMeta = readExitFlowMeta(current?.params)

  // replace = подмена текущего экрана, глубина стека та же
  if (options?.isReplace) {
    if (currentMeta) {
      return { origin: currentMeta.origin, hop: currentMeta.hop }
    }

    if (isFlowRoot(currentName)) {
      return { origin: currentName as ScreensList, hop: 1 }
    }

    return { origin: ScreensList.HOME_START, hop: 1 }
  }

  if (isFlowRoot(currentName)) {
    return { origin: currentName as ScreensList, hop: 1 }
  }

  if (currentMeta) {
    return { origin: currentMeta.origin, hop: currentMeta.hop + 1 }
  }

  // Диплинк / холодный вход без meta: не рисуем крестик на лендинге (hop=1)
  return { origin: ScreensList.HOME_START, hop: 1 }
}

/**
 * Кладёт meta в leaf-params (поддерживает nested `{ screen, params }` как на RN).
 */
export function injectExitFlowMeta(params: unknown, meta: ExitFlowMeta): Record<string, unknown> {
  if (params && typeof params === 'object' && 'screen' in (params as object)) {
    const nested = params as { screen: unknown; params?: unknown; [key: string]: unknown }

    return {
      ...nested,
      params: injectExitFlowMeta(nested.params, meta),
    }
  }

  const base =
    params && typeof params === 'object' && !Array.isArray(params) ? { ...(params as Record<string, unknown>) } : {}

  return {
    ...base,
    [EXIT_FLOW_PARAM_KEY]: meta,
  }
}

/**
 * Для nested navigate `{ screen, params }` возвращает leaf-ключ целевого экрана.
 */
export function resolveLeafTargetKey(key: NavKey | string, params: unknown): string {
  let currentKey = String(key)
  let currentParams = params

  while (currentParams && typeof currentParams === 'object' && 'screen' in (currentParams as object)) {
    const nested = currentParams as { screen: unknown; params?: unknown }

    if (typeof nested.screen !== 'string') break

    currentKey = nested.screen
    currentParams = nested.params
  }

  return currentKey
}

export type WithExitFlowParamsInput = {
  targetKey: NavKey | string
  params: unknown
  current: CurrentRouteSnapshot
  options?: ExitFlowInjectOptions
}

/**
 * Полный цикл: вычислить meta и вмержить в params перед navigate/replace.
 */
export function withExitFlowParams(input: WithExitFlowParamsInput): Record<string, unknown> {
  const { targetKey, params, current, options } = input
  const leafKey = resolveLeafTargetKey(targetKey, params)
  const meta = computeNextExitFlowMeta(leafKey, current, options)

  return injectExitFlowMeta(params, meta)
}

export type ExitCrossState = {
  isVisible: boolean
  origin: ScreensList | undefined
}

// Крестик при hop >= 2; на flow-root экране — по входящей цепочке (entry)
export function getExitCrossState(meta: ExitFlowMeta | undefined): ExitCrossState {
  if (!meta) return { isVisible: false, origin: undefined }

  if (meta.hop >= 2) return { isVisible: true, origin: meta.origin }

  if (meta.entry && meta.entry.hop >= 2) return { isVisible: true, origin: meta.entry.origin }

  return { isVisible: false, origin: undefined }
}

/**
 * Табы внутри `AUTHORIZED`. Origin из этого множества сбрасываем как
 * `AUTHORIZED / {tab}`; остальные FLOW_ROOTS (Settings / Security / Paygine) —
 * экраны корневого стека рядом с табами, не внутри них.
 */
export const AUTHORIZED_TAB_ROOTS: ReadonlySet<string> = new Set([ScreensList.HOME_START, ScreensList.HOME_START])

export function isAuthorizedTabRoot(key: string | undefined | null): boolean {
  return !!key && AUTHORIZED_TAB_ROOTS.has(key)
}

export type ExitFlowResetRoute = {
  name: string
  params?: unknown
  state?: { index: number; routes: Array<{ name: string }> }
}

export type ExitFlowResetState = {
  index: number
  routes: ExitFlowResetRoute[]
}

/**
 * Состояние для `CommonActions.reset` к origin exit-cross.
 *
 * - Таб (Home / Chats / Services / Profile / …) → один роут `AUTHORIZED` с этим табом.
 * - Stack-root (Settings / Security / Paygine) → `AUTHORIZED(Profile)` + origin сверху.
 *   Иначе mobile кладёт несуществующий таб в TabNavigator и падает в `HOME_START`.
 */
export function buildExitFlowResetState(origin: ScreensList): ExitFlowResetState {
  if (isAuthorizedTabRoot(origin)) {
    return {
      index: 0,
      routes: [
        {
          name: StackMode.AUTHORIZED,
          state: { index: 0, routes: [{ name: origin }] },
        },
      ],
    }
  }

  return {
    index: 1,
    routes: [
      {
        name: StackMode.AUTHORIZED,
        state: { index: 0, routes: [{ name: ScreensList.HOME_START }] },
      },
      { name: origin },
    ],
  }
}
